<template>
  <el-upload
    v-if="option.config.isAvatar"
    class="avatar-uploader"
    :headers="headers"
    :action="upLoadUrl"
    :accept="accept"
    :show-file-list="option.config.showFileList || false"
    :on-success="handleSuccess"
    :on-error="handleError"
    :before-upload="beforeUpload"
    :on-exceed="handleExceed"
  >
    <template v-if="value">
      <ZPreview :authSrc="value" :headers="headers" :baseUrl="upLoadUrl" :showPreview="false"/>
    </template>
    <el-icon v-else class="avatar-uploader-icon"><plus /></el-icon>
  </el-upload>
  <div v-else class="z-upload-wrapper" @paste="handlePaste">
    <!-- 统一上传区域：点击/拖拽/粘贴 -->
    <div
      ref="uploadArea"
      class="upload-area"
      :class="{ 'upload-area--dragover': isDragOver }"
      @click="handleAreaClick"
      @dragover.prevent="isDragOver = true"
      @dragleave="isDragOver = false"
      @drop="handleDrop"
    >
      <input
        ref="fileInput"
        type="file"
        :accept="accept"
        multiple
        style="display: none"
        @change="handleFileSelect"
      />

      <!-- 无文件时显示上传提示 -->
      <template v-if="!value || !value.length">
        <el-icon class="upload-area-icon"><upload-filled /></el-icon>
        <div class="upload-area-text">点击或拖拽、粘贴上传图片</div>
        <div class="upload-area-hint">支持拖拽、点击选择、粘贴，最多{{ limit }}张，单张不超过 50MB</div>
      </template>

      <!-- 有文件时显示文件网格 -->
      <ul v-else class="upload-file-grid">
        <li v-for="(item, index) in value" :key="index" class="grid-item">
          <div class="grid-item-preview" @click.stop>
            <ZPreview
              :authSrc="item.url"
              :headers="headers"
              :baseUrl="upLoadUrl"
              :width="String(80)"
              :height="String(80)"
              v-if="item.fileType === 'image'"
            />
            <template v-else>
              <svg class="icon font-40" aria-hidden="true">
                <use :xlink:href="item.fileType" />
              </svg>
            </template>
            <div class="grid-item-delete" @click.stop="handleDelete(index, item.url)">
              <el-icon><close /></el-icon>
            </div>
          </div>
          <div class="grid-item-name">{{ item.url }}</div>
          <div class="grid-item-actions">
            <el-link :underline="false" @click.stop="downLoadFile(item.url)" type="primary">下载</el-link>
            <el-link :underline="false" @click.stop="handleDelete(index, item.url)" type="danger">删除</el-link>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>
<script lang='ts'>
import { defineComponent, reactive, toRefs, watch, ref, onMounted, onBeforeUnmount } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { UploadFilled, Close } from '@element-plus/icons-vue'
import ZPreview from './ZPreview.vue'
import axios from 'axios'
import { BASE_URL } from '@/utils/httpClient'

export default defineComponent({
  name: 'ZUpload',
  components: {
    ZPreview
  },
  props: {
    value: { type: [String, Array], default: '' },
    option: { type: Object, required: true, default: () => null },
    canUpload: { type: Boolean, required: false, default: true },
    accept: { type: String, default: '' }
  },
  emits: ['changed'],

  setup(props, context) {
    const $api = BASE_URL
    const uploadArea = ref<HTMLElement | null>(null)
    const fileInput = ref<HTMLInputElement | null>(null)
    const isDragOver = ref(false)

    const getExtensionFileName = (pathfilename: string) => {
      const reg = /(\\+)/g
      const pString = pathfilename.replace(reg, '#')
      const arr = pString.split('#')
      const lastString = arr[arr.length - 1]
      const arr2 = lastString.split('.')
      return arr2[arr2.length - 1]
    }
    const fileTypeNumber = (name: string) => {
      const fileType = getExtensionFileName(name)
      let type = ''
      if (fileType === 'jpg' || fileType === 'png' || fileType === 'bmp' || fileType === 'jpeg' || fileType === 'gif' || fileType === 'webp') {
        type = 'image'
      } else if (fileType === 'doc' || fileType === 'docx') {
        type = '#zwj-Word'
      } else if (fileType === 'pdf') {
        type = '#zwj-Pdf'
      } else if (fileType === 'xlsx' || fileType === 'xls') {
        type = '#zwj-Excel'
      } else if (fileType === 'ppt' || fileType === 'pptx') {
        type = '#zwj-PPT'
      } else if (fileType === 'zip' || fileType === 'rar') {
        type = '#zwj-yasuobao'
      } else {
        type = '#zwj-icon-test'
      }
      return type
    }
    const hanldeFileArray = (array: any) => {
      let returnArray = []
      if (!Array.isArray(array)) {
        returnArray = array
      } else {
        returnArray = array.map((k: any) => {
          return {
            url: k,
            fileType: fileTypeNumber(k)
          }
        })
      }
      return returnArray
    }
    const uploadData = reactive({
      upLoadUrl: $api + '/upload',
      headers: {
        'X-APP-ID': 'WEB'
      },
      limit: props.option.config.limit || 50,
      option: props.option,
      loading: false,
      value: hanldeFileArray(props.value),
      canUpload: props.canUpload,

      triggerFileInput() {
        fileInput.value?.click()
      },
      handleAreaClick(event: MouseEvent) {
        this.triggerFileInput()
      },
      handleFileSelect(event: any) {
        const files = Array.from(event.target.files || []) as File[]
        if (files.length === 0) return
        uploadData.uploadFiles(files)
        // 清空 input 允许重复选择同一文件
        event.target.value = ''
      },
      handleSuccess(response: any, file: any, fileList: any) {
        if (response) {
          let list: string[] = []
          if (props.option.config.limit && props.option.config.limit === 1) {
            list = response.files.file
          } else {
            list = fileList.map((k: any) => {
              return k.response ? k.response.files.file : k.url
            })
          }
          context.emit('changed', list)
        } else {
          ElMessage.error('上传失败！')
        }
      },

      handleExceed(files: any, fileList: any) {
        ElMessage.warning(`当前限制选择${uploadData.option.config.limit}个文件，本次选择了 ${files.length} 个文件，共选择了 ${files.length + fileList.length} 个文件`)
      },
      beforeUpload(file: any) {
        const isLt2M = file.size / 1024 / 1024 < 50
        if (!isLt2M) {
          ElMessage.error('上传文件大小不能超过 50MB')
        }
        return isLt2M
      },
      handleError(err: any, file: any, fileList: any) {
        const error = (err && err.message) ? JSON.parse(err.message) : { message: '上传失败' }
        if (Array.isArray(fileList)) {
          const idx = fileList.findIndex((f: any) => f.uid === (file as any).uid)
          if (idx !== -1) fileList.splice(idx, 1)
        }
        if (error.status === 413) {
          ElMessage.error('附件太大！')
        } else if (error.status === 400) {
          ElMessage.error(error.message)
        } else {
          ElMessage.error(error.message)
        }
      },
      handleDelete(index: number, fileName: string) {
        ElMessageBox.confirm(`确定移除 ${fileName}？`).then(() => {
          uploadData.value.splice(index, 1)
          const list = uploadData.value.map((k: any) => k.url)
          context.emit('changed', list)
        })
      },
      handleDrop(event: any) {
        isDragOver.value = false
        const files = event.dataTransfer?.files
        if (!files || files.length === 0) return
        const imageFiles = Array.from(files).filter((f: any) => f.type.startsWith('image/'))
        if (imageFiles.length === 0) {
          ElMessage.error('拖拽内容非图片')
          return
        }
        uploadData.uploadFiles(Array.from(files) as unknown as File[])
      },
      handlePaste(event: any) {
        const items = (event.clipboardData || (window as any).clipboardData).items
        if (!items || items.length === 0) {
          ElMessage.error('当前浏览器不支持本地')
          return
        }
        const pasteFile = (Array.from(items).find((item: any) => item.type.indexOf('image') !== -1) as DataTransferItem)?.getAsFile()
        if (!pasteFile) {
          ElMessage.error('粘贴内容非图片')
          return
        }
        uploadData.uploadFiles([pasteFile as File])
      },
      uploadFiles(files: File[]) {
        files.forEach((file: File) => {
          const formData = new FormData()
          formData.append('file', file)
          axios.post(uploadData.upLoadUrl, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              'X-APP-ID': 'WEB'
            }
          })
            .then(response => {
              const currentFiles = [...uploadData.value, { response: response.data }]
              uploadData.handleSuccess(response.data, formData, currentFiles)
            })
            .catch(error => {
              uploadData.handleError(error, formData, uploadData.value)
            })
        })
      },
      downLoadFile(url: string) {
        if (!url) return
        const request = new XMLHttpRequest()
        request.responseType = 'blob'
        request.open('get', $api + url, true)
        request.setRequestHeader('X-APP-ID', uploadData.headers['X-APP-ID'])
        request.onreadystatechange = () => {
          if (request.readyState === XMLHttpRequest.DONE && request.status === 200) {
            const _url = URL.createObjectURL(request.response)
            const alink = document.createElement('a')
            alink.download = '上海蕊恺电子' + url
            alink.href = _url
            alink.target = '_blank'
            alink.click()
          }
        }
        request.send(null)
      }
    })
    watch(() => props.value, () => {
      uploadData.value = hanldeFileArray(props.value)
    })
    onMounted(() => {
      // 全局粘贴监听已在模板 @paste 中处理
    })

    return { ...toRefs(uploadData), uploadArea, fileInput, isDragOver, UploadFilled, Close }
  }
})

</script>
<style lang="scss">
.avatar-uploader .el-upload {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
}
.avatar-uploader .el-upload:hover {
  border-color: #409eff;
}
.avatar-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  width: 178px;
  height: 178px;
  text-align: center;
}
.avatar-uploader-icon svg {
  margin-top: 74px;
}
.avatar {
  width: 178px;
  height: 178px;
  display: block;
}
.z-upload-wrapper {
  width: 100%;
}

/* 统一上传区域样式 */
.upload-area {
  width: 100%;
  border: 2px dashed #dcdfe6;
  border-radius: 8px;
  padding: 30px 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: #fafafa;
  text-align: center;

  &:hover {
    border-color: #409eff;
    background-color: #f5f7fa;
  }

  &--dragover {
    border-color: #409eff;
    background-color: #ecf5ff;
  }
}

.upload-area-icon {
  font-size: 48px;
  color: #c0c4cc;
  margin-bottom: 12px;
}

.upload-area-text {
  font-size: 14px;
  color: #606266;
  margin-bottom: 4px;
}

.upload-area-hint {
  font-size: 12px;
  color: #909399;
}

/* 文件网格样式 */
.upload-file-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
  padding: 0;
  margin: 0;
  list-style: none;
}

.grid-item {
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #fff;
  transition: box-shadow 0.3s;

  &:hover {
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  }
}

.grid-item-preview {
  position: relative;
  width: 80px;
  height: 80px;
  margin-bottom: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.grid-item-delete {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 20px;
  height: 20px;
  background-color: #f56c6c;
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 12px;
  opacity: 0;
  transition: opacity 0.2s;

  .grid-item:hover & {
    opacity: 1;
  }

  &:hover {
    background-color: #e64242;
  }
}

.grid-item-name {
  font-size: 11px;
  color: #606266;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-bottom: 4px;
  text-align: left;
}

.grid-item-actions {
  display: flex;
  gap: 8px;
  font-size: 11px;
}
</style>
